import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Trophy, Eye, EyeOff, Loader2, Mail, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LoginPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [needsVerification, setNeedsVerification] = useState(false);
    const [resendingVerification, setResendingVerification] = useState(false);
    const [verificationSent, setVerificationSent] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const from = location.state?.from?.pathname || '/';

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setNeedsVerification(false);
        setVerificationSent(false);
        
        try {
            await login(email, password);
            toast.success('Welcome back!');
            navigate(from, { replace: true });
        } catch (error) {
            const errorMessage = error.response?.data?.detail || 'Login failed';
            
            // Check if the error is about email verification
            if (errorMessage.toLowerCase().includes('verify')) {
                setNeedsVerification(true);
            }
            
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleResendVerification = async () => {
        if (!email) {
            toast.error('Please enter your email address first');
            return;
        }
        
        setResendingVerification(true);
        try {
            await axios.post(`${API_URL}/api/auth/resend-verification`, { email });
            setVerificationSent(true);
            toast.success('Verification email sent! Check your inbox.');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to send verification email');
        } finally {
            setResendingVerification(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center px-4 py-12">
            <div className="w-full max-w-md">
                <Card>
                    <CardHeader className="text-center">
                        <Link to="/" className="inline-flex items-center justify-center gap-2 mb-4">
                            <Trophy className="h-8 w-8 text-primary" />
                            <span className="font-heading font-bold text-2xl tracking-wide">RaceWise</span>
                        </Link>
                        <CardTitle className="text-xl font-semibold">Welcome back</CardTitle>
                        <CardDescription>Sign in to your account to continue</CardDescription>
                    </CardHeader>
                <CardContent>
                    {needsVerification && !verificationSent && (
                        <Alert className="mb-4 border-amber-500/50 bg-amber-500/10">
                            <Mail className="h-4 w-4 text-amber-500" />
                            <AlertDescription className="text-amber-500">
                                <p className="mb-2">Please verify your email before logging in.</p>
                                <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={handleResendVerification}
                                    disabled={resendingVerification}
                                    className="border-amber-500/50 text-amber-500 hover:bg-amber-500/20"
                                >
                                    {resendingVerification ? (
                                        <>
                                            <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                                            Sending...
                                        </>
                                    ) : (
                                        <>
                                            <Send className="mr-2 h-3 w-3" />
                                            Resend Verification Email
                                        </>
                                    )}
                                </Button>
                            </AlertDescription>
                        </Alert>
                    )}
                    
                    {verificationSent && (
                        <Alert className="mb-4 border-green-500/50 bg-green-500/10">
                            <Mail className="h-4 w-4 text-green-500" />
                            <AlertDescription className="text-green-500">
                                Verification email sent! Please check your inbox and click the link to verify your account.
                            </AlertDescription>
                        </Alert>
                    )}
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="you@example.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                data-testid="login-email"
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label htmlFor="password">Password</Label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    data-testid="login-password"
                                />
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="absolute right-0 top-0 h-full px-3"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </Button>
                            </div>
                        </div>

                        <Button 
                            type="submit" 
                            className="w-full btn-pill" 
                            disabled={loading}
                            data-testid="login-submit"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Signing in...
                                </>
                            ) : (
                                'Sign In'
                            )}
                        </Button>
                    </form>

                    <div className="mt-4 text-center">
                        <Link to="/forgot-password" className="text-sm text-muted-foreground hover:text-primary" data-testid="forgot-password-link">
                            Forgot your password?
                        </Link>
                    </div>

                    <div className="mt-6 text-center text-sm">
                        <span className="text-muted-foreground">Don&apos;t have an account? </span>
                        <Link to="/register" className="text-primary hover:underline font-medium" data-testid="register-link">
                            Sign up
                        </Link>
                    </div>
                </CardContent>
            </Card>
            
            {/* Powered by Podium */}
            <div className="mt-6 flex flex-col items-center justify-center gap-2 text-muted-foreground">
                <span className="text-sm">Powered by</span>
                <img 
                    src="https://media.ffycdn.net/eu/pa-media-group/UDcy2NnpYEbTrc3irv2X.png?width=600&format=webp&quality=100" 
                    alt="Podium" 
                    className="h-8 w-auto"
                />
            </div>
            </div>
        </div>
    );
}
